import { Page } from '@app/components/page/Page';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { MRT_PaginationState, MRT_SortingState } from 'material-react-table';
import { ComponentType, useCallback, useMemo } from 'react';
import { ListViewContext } from './context/ListViewContext';
import { DataToolbar } from './DataToolbar';
import { applyLocalFilters } from './helpers/apply-local-filters';
import { useListViewLogic, UseListViewLogicProps } from './hooks/useListViewLogic';
import { ListEmptyState } from './ListEmptyState';
import { PaginationChangeHandler, SortingChangeHandler } from './ListViewPaged';
import { ListViewFilters } from './types';

/** React Query key segment for the full in-memory source dataset. */
export const LIST_VIEW_LOCAL_SOURCE_KEY = 'source' as const;

export interface ListViewLocalChildProps<TData> {
  data: TData[];
  total: number;
  sorting: MRT_SortingState;
  onSortingChange: SortingChangeHandler;
  isLoading?: boolean;
  isFetching?: boolean;
  showProgress?: boolean;
  /** Only set when `enablePagination` is true. */
  pagination?: MRT_PaginationState;
  /** Only set when `enablePagination` is true. */
  onPaginationChange?: PaginationChangeHandler;
}

export interface ListViewLocalProps<
  TData,
  _TFilters extends ListViewFilters,
> extends UseListViewLogicProps {
  /**
   * Loads the full dataset once. Cached under `[listId, 'source']`.
   * Must take no React Query context args — Kubb axios helpers interpret `client` as HTTP client.
   * Filter and page changes do not re-call this function.
   */
  queryFn: () => Promise<TData[]>;
  ListComponent: ComponentType<ListViewLocalChildProps<TData>>;
  /**
   * When false, all filtered rows are passed to `ListComponent` and pagination
   * props are omitted. Defaults to true.
   */
  enablePagination?: boolean;
  actions?: React.ReactNode[];
  showProgress?: boolean;
  ListWrapper?: ComponentType<{ children: React.ReactNode }>;
}

/**
 * ListView variant for client-side filtering (and optional pagination) over a
 * full in-memory dataset. Uses FilterDefinition.filterFn for matching.
 */
export function ListViewLocal<TData, _TFilters extends ListViewFilters>({
  filters,
  listId,
  queryFn,
  ListComponent,
  enablePagination = true,
  actions = [],
  defaultPageSize = 50,
  stickyFilters = true,
  exportFn,
  showProgress = true,
  ListWrapper = Page.Content,
}: ListViewLocalProps<TData, _TFilters>) {
  const queryClient = useQueryClient();

  const { allParams, setAllParams, contextValue, onExport, handleSortingChange, sorting } =
    useListViewLogic({
      listId,
      filters,
      defaultPageSize,
      stickyFilters,
      exportFn,
    });

  const sourceQueryKey = useMemo(() => [listId, LIST_VIEW_LOCAL_SOURCE_KEY] as const, [listId]);

  const {
    data: items = [],
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: sourceQueryKey,
    // Never forward QueryFunctionContext — Kubb axios helpers treat `client` as the HTTP client.
    queryFn: () => queryFn(),
  });

  const { pageIndex, pageSize, sort: _sort, ...urlFilters } = allParams;

  const filtered = useMemo(
    () => applyLocalFilters(items, filters ?? [], urlFilters),
    [items, filters, urlFilters],
  );

  const pageData = useMemo(() => {
    if (!enablePagination) {
      return filtered;
    }
    const start = pageIndex * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [enablePagination, filtered, pageIndex, pageSize]);

  const handlePaginationChange: PaginationChangeHandler = useCallback(
    (updaterOrValue) => {
      const newPagination =
        typeof updaterOrValue === 'function'
          ? updaterOrValue({ pageIndex, pageSize })
          : updaterOrValue;
      setAllParams((old) => ({
        ...old,
        pageIndex: newPagination.pageIndex,
        pageSize: newPagination.pageSize,
      }));
    },
    [pageIndex, pageSize, setAllParams],
  );

  const onRefresh = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: sourceQueryKey });
  }, [queryClient, sourceQueryKey]);

  const total = filtered.length;
  const hasNoResults = !isLoading && total === 0;

  return (
    <ListViewContext.Provider value={contextValue}>
      <DataToolbar
        actions={actions}
        onExport={onExport}
        onRefresh={onRefresh}
        total={total}
        isLoading={isFetching}
        showProgress={showProgress}
      />
      <ListWrapper>
        {hasNoResults ? (
          <ListEmptyState />
        ) : (
          <ListComponent
            data={pageData}
            total={total}
            isLoading={isLoading}
            isFetching={isFetching}
            sorting={sorting}
            onSortingChange={handleSortingChange}
            showProgress={false}
            {...(enablePagination
              ? {
                  pagination: { pageIndex, pageSize },
                  onPaginationChange: handlePaginationChange,
                }
              : {})}
          />
        )}
      </ListWrapper>
    </ListViewContext.Provider>
  );
}
